<template>
	<sg-modal
		v-model="isOpen"
		title="Invite Results"
		:backdrop-dismiss="false"
		@did-dismiss="closeModal"
	>
		<div class="ion-padding">
			<!-- Summary -->
			<p
				v-if="results.successCount > 0 && results.failed.length > 0"
				class="mb-4 text-sm text-gray-500"
			>
				{{ results.successCount }} successful,
				{{ results.failed.length }} failed
			</p>
			<p
				v-if="results.successCount > 0"
				class="mb-4 text-sm text-gray-500"
			>
				Share these invite links with your managed users. They can use
				these links to set up their accounts.
			</p>
			<p v-else class="mb-4 text-sm text-gray-500">
				All invites failed. Please review the errors below.
			</p>

			<!-- Successful Invites Table -->
			<div
				v-if="results.succeeded.length > 0"
				class="mb-6 overflow-x-auto rounded-lg border border-gray-200"
			>
				<table class="w-full">
					<thead class="bg-gray-50">
						<tr>
							<th
								class="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700"
							>
								Username
							</th>
							<th
								class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700"
							>
								Invite Link
							</th>
							<th
								class="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700"
							>
								Actions
							</th>
						</tr>
					</thead>
					<tbody class="divide-y divide-gray-200 bg-white">
						<tr
							v-for="invite in results.succeeded"
							:key="invite.userName"
							class="hover:bg-gray-50"
						>
							<td
								class="w-1 whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900"
							>
								<div class="truncate">
									{{ invite.userName }}
								</div>
							</td>
							<td class="max-w-0 px-4 py-3 text-sm text-gray-700">
								<div
									class="truncate"
									:title="invite.signupLink"
								>
									{{ invite.signupLink }}
								</div>
							</td>
							<td class="w-1 whitespace-nowrap px-4 py-3 text-sm">
								<div class="flex flex-col gap-2 sm:flex-row">
									<CopyInviteButton :invite="invite" />
									<ShareInviteButton :invite="invite" />
									<ion-button
										size="small"
										fill="outline"
										color="tertiary"
										@click="
											openQRModal(
												invite.userName,
												invite.signupLink,
											)
										"
									>
										<ion-icon
											slot="start"
											:icon="qrCodeOutline"
										/>
										QR Code
									</ion-button>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Failed Invites Table -->
			<template v-if="results.failed.length > 0">
				<h3 class="mb-4 pl-3 text-sm font-bold text-red-700">
					The following invites failed:
				</h3>
				<div class="overflow-x-auto rounded-lg border border-gray-200">
					<table class="w-full">
						<thead class="bg-gray-50">
							<tr>
								<th
									class="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700"
								>
									Username
								</th>
								<th
									class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700"
								>
									Error
								</th>
							</tr>
						</thead>
						<tbody class="divide-y divide-gray-200 bg-white">
							<tr
								v-for="failure in results.failed"
								:key="failure.userName"
								class="hover:bg-gray-50"
							>
								<td
									class="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900"
								>
									{{ failure.userName }}
								</td>
								<td class="px-4 py-3 text-sm text-gray-600">
									{{
										$t(
											getInviteErrorTranslationKey(
												failure.errorCode,
											),
										)
									}}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</template>

			<!-- QR Code Modal -->
			<InviteQRCodeModal
				v-model="qrModalOpen"
				:username="selectedInvite.userName"
				:invite-url="selectedInvite.signupLink"
			/>
		</div>

		<template #footer>
			<div class="flex justify-end">
				<ion-button @click="closeModal">Done</ion-button>
			</div>
		</template>
	</sg-modal>
</template>

<script setup lang="ts">
	import { IonButton, IonIcon } from "@ionic/vue";
	import { qrCodeOutline } from "ionicons/icons";
	import SgModal from "@/components/ui/modals/SgModal.vue";
	import InviteQRCodeModal from "@/components/feature/auth/invite/InviteQRCodeModal.vue";
	import CopyInviteButton from "@/components/feature/auth/invite/CopyInviteButton.vue";
	import ShareInviteButton from "@/components/feature/auth/invite/ShareInviteButton.vue";
	import { ref } from "vue";
	import type { BulkInviteResultDTO } from "seedgreen-shared/dtos/InviteDTO";
	import { getInviteErrorTranslationKey } from "seedgreen-shared/dtos/InviteDTO";

	const isOpen = ref<boolean>(false);
	const results = ref<BulkInviteResultDTO>({
		succeeded: [],
		failed: [],
		totalProcessed: 0,
		successCount: 0,
	});

	const qrModalOpen = ref<boolean>(false);
	const selectedInvite = ref<{ userName: string; signupLink: string }>({
		userName: "",
		signupLink: "",
	});

	const openModal = (inviteResults: BulkInviteResultDTO) => {
		results.value = inviteResults;
		isOpen.value = true;
	};

	const closeModal = () => {
		isOpen.value = false;
		results.value = {
			succeeded: [],
			failed: [],
			totalProcessed: 0,
			successCount: 0,
		};
	};

	const openQRModal = (userName: string, link: string) => {
		selectedInvite.value = {
			userName,
			signupLink: link,
		};
		qrModalOpen.value = true;
	};

	defineExpose({
		openModal,
		closeModal,
	});
</script>

<style scoped>
	ion-modal {
		--width: 60rem;
	}
</style>
